import { useCallback, useEffect, useRef, useState } from "react"
import { LexicalText, Meeting } from "@/schema"
import { NoteTemplate } from "@/schema/note-template"
import { Button } from "@ui/shad"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@ui/tab-pages"
import { Check, Copy, FileText, Loader2, Lock, MessageCircle, NotebookPen } from "lucide-react"

import { isMeetingPromptInProgress, meetingPromptStatus } from "@/lib/prompt-status"
import { useRenderNoteTemplate } from "@/hooks/api/company-note-templates"
import { useUpdateMeeting } from "@/hooks/api/meetings"
import { Editor, EditorRefHandle } from "@/components/lexical"
import { MeetingNotepadHistoryButton } from "@/components/meetings/meeting-version-dialog"
import { SummaryNotation } from "@/components/meetings/summary-notation"
import { NoteTemplatePicker } from "@/components/notes/note-template-picker"
import { PromptButtons } from "@/components/prompts/button-container"

import MeetingTypeSelector from "./meeting-type-selector"
import { MeetingChat } from "./recap/meeting-chat"

export default function NotesAiSection({
  meeting,
  isLoading,
  editorRevision = 0,
}: {
  meeting?: Meeting
  isLoading: boolean
  editorRevision?: number
}) {
  const consentGated = Boolean(meeting?.consent_gated)
  const [copied, setCopied] = useState(false)
  const [activeTab, setActiveTab] = useState(consentGated ? "notepad" : "chat")

  useEffect(() => {
    if (consentGated && activeTab === "chat") setActiveTab("notepad")
  }, [consentGated, activeTab])
  const updateMeeting = useUpdateMeeting()
  const renderTemplate = useRenderNoteTemplate()
  const editorRef = useRef<EditorRefHandle>(null)

  const handleCopyMarkdown = async () => {
    if (meeting?.ai_summary?.markdown) {
      const notice = meeting.ai_notice ? `\n\n_${meeting.ai_notice}_` : ""
      await navigator.clipboard.writeText(`${meeting.ai_summary.markdown}${notice}`)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleNotepadChange = (content: LexicalText) => {
    if (meeting?.id) {
      updateMeeting.mutate({
        meetingId: meeting.id,
        data: { notepad: content },
      })
    }
  }

  const handleApplyTemplate = useCallback(
    (template: NoteTemplate) => {
      if (!template.content || !meeting) return

      const applyContent = (content: LexicalText) => {
        editorRef.current?.updateContent(content)
        handleNotepadChange(content)
      }

      renderTemplate.mutate(
        { noteTemplateId: template.id, meetingId: meeting.id },
        {
          onSuccess: (data) => {
            if (data.note_template_render.content) {
              applyContent(data.note_template_render.content)
            } else {
              applyContent(template.content!)
            }
          },
          onError: () => {
            applyContent(template.content!)
          },
        }
      )
    },
    [meeting, renderTemplate] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const handleAiSummaryChange = (content: LexicalText) => {
    if (meeting?.id) {
      updateMeeting.mutate({
        meetingId: meeting.id,
        data: { ai_summary: content },
      })
    }
  }

  const hasAiSummaryContent = Boolean(meeting?.ai_summary?.html || meeting?.ai_summary?.markdown)
  const aiSummaryStatus = meetingPromptStatus(meeting, "Prompt::MeetingNote")
  const isGeneratingAiSummary = isMeetingPromptInProgress(meeting, "Prompt::MeetingNote")
  const hasAiSummaryFailed = aiSummaryStatus === "failed"

  return (
    <div className="relative flex h-full flex-col">
      {meeting && (
        <div className="absolute top-[5px] right-2 z-10 flex items-center gap-1">
          <p className="text-secondary-foreground hidden text-sm lg:block">Type:</p>
          <MeetingTypeSelector meeting={meeting} />
        </div>
      )}
      <Tabs
        defaultValue={consentGated ? "notepad" : "chat"}
        className="flex-1"
        value={activeTab}
        onValueChange={setActiveTab}
      >
        <TabsList className="px-4">
          {!consentGated && (
            <TabsTrigger value="chat" icon={<MessageCircle />}>
              Chat
            </TabsTrigger>
          )}
          <TabsTrigger value="notes" icon={<FileText />}>
            AI Summary
          </TabsTrigger>
          <TabsTrigger value="notepad" icon={<NotebookPen />}>
            Notepad
          </TabsTrigger>
        </TabsList>
        <TabsContent value="chat">
          {meeting?.id && !consentGated && <MeetingChat meetingId={meeting.id} meetingType={meeting.meeting_type} />}
        </TabsContent>
        <TabsContent value="notes" className="relative px-6 pt-4">
          {activeTab === "notes" && meeting?.ai_summary?.markdown && (
            <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
              {meeting?.id && (
                <PromptButtons
                  size="sm"
                  promptType="Prompt::MeetingNote"
                  recordId={meeting.id}
                  bookId={meeting.book_id}
                  meetingType={meeting.meeting_type}
                  isPromptRunInProgress={isGeneratingAiSummary}
                />
              )}
              <Button onClick={handleCopyMarkdown} variant="outline" size="iconSm" title="Copy markdown">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          )}
          {!isLoading && hasAiSummaryContent && (
            <div className="pb-24">
              <Editor
                key={`${meeting?.id}-${editorRevision}`}
                markdown={meeting?.ai_summary?.markdown}
                initialContent={meeting?.ai_summary}
                editable={true}
                onChange={handleAiSummaryChange}
              />
              <SummaryNotation aiNotice={meeting?.ai_notice} />
            </div>
          )}
          {!isLoading && isGeneratingAiSummary && (
            <div className="flex items-center justify-center gap-2 py-3 text-sm">
              <Loader2 className="size-4 animate-spin" />
              <span className="text-muted-foreground">Generating AI summary...</span>
            </div>
          )}
          {!isLoading && hasAiSummaryFailed && (
            <p className="text-red-foreground text-sm">AI summary generation failed. Please retry.</p>
          )}
          {!isLoading && consentGated && (
            <p className="text-muted-foreground inline-flex items-center gap-1.5 text-sm">
              <Lock className="size-4" />
              AI notes are available after you confirm client consent.
            </p>
          )}
          {!isLoading && !consentGated && !isGeneratingAiSummary && !hasAiSummaryFailed && !hasAiSummaryContent && (
            <p className="text-muted-foreground text-sm">No AI summary available.</p>
          )}
        </TabsContent>
        <TabsContent value="notepad" className="relative px-6 pt-4 pb-12">
          {!isLoading && meeting?.id && (
            <>
              <div className="absolute top-4 right-6 z-10 flex gap-2">
                <MeetingNotepadHistoryButton meetingId={meeting.id} />
                <NoteTemplatePicker onApply={handleApplyTemplate} />
              </div>
              <Editor
                ref={editorRef}
                key={`${meeting?.id}-notepad-${editorRevision}`}
                markdown={meeting?.notepad?.markdown}
                initialContent={meeting?.notepad}
                editable={true}
                onChange={handleNotepadChange}
                placeholder="Take notes here..."
              />
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
